import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatCurrency, getCurrency, convertCurrency } from './utils/currency';
import './HotelDetailsPage.css';

const HotelDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hotel, searchParams } = location.state || {};

  // Debug: Log hotel data
  React.useEffect(() => {
    if (hotel) {
      console.log('🏨 Hotel Details Page - Hotel Data:', {
        name: hotel.name,
        hotelId: hotel.hotelId,
        media: hotel.media,
        mediaLength: hotel.media?.length,
        hasMedia: !!hotel.media && hotel.media.length > 0,
        address: hotel.address,
        hotelDistance: hotel.hotelDistance,
        searchParams: searchParams
      });
    }
  }, [hotel, searchParams]);

  if (!hotel) {
    return (
      <div className="hotel-details-page">
        <Header />
        <div className="hotel-details-error">
          <h2>Hotel Not Found</h2>
          <p>No hotel information available.</p>
          <button onClick={() => navigate('/hotels')}>Back to Hotels</button>
        </div>
        <Footer />
      </div>
    );
  }

  const formatPriceDisplay = (price) => {
    if (!price || !price.total) return 'Price not available';
    
    const userCurrency = getCurrency() || { code: 'NGN', symbol: '₦' };
    const apiCurrency = (price.currency || searchParams?.currency || 'NGN').toUpperCase();
    const apiAmount = parseFloat(price.total);
    
    if (isNaN(apiAmount) || apiAmount <= 0) {
      return 'Price not available';
    }
    
    if (apiCurrency === userCurrency.code) {
      return formatCurrency(apiAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    }
    
    try {
      const convertedAmount = convertCurrency(apiAmount, apiCurrency, userCurrency.code);
      
      if (!convertedAmount || convertedAmount <= 0) {
        return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      
      return formatCurrency(convertedAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    } catch (error) {
      return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="hotel-details-page">
      <Header />
      
      <div className="hotel-details-container">
        <button className="back-button" onClick={() => navigate('/hotels')}>
          ← Back to Hotels
        </button>

        {/* Hotel Header */}
        <div className="hotel-header-section">
          <div className="hotel-title-section">
            <h1 className="hotel-main-title">{hotel.name}</h1>
            {hotel.hotelId && (
              <span className="hotel-id-badge">ID: {hotel.hotelId}</span>
            )}
            {hotel.rating && (
              <div className="hotel-rating-display">
                <span className="rating-stars-display">
                  {'⭐'.repeat(Math.floor(hotel.rating))}
                  {hotel.rating % 1 !== 0 && <span>½</span>}
                </span>
                <span className="rating-number">{hotel.rating.toFixed(1)}/5</span>
              </div>
            )}
          </div>
          {hotel.price && (
            <div className="hotel-price-display">
              <div className="price-main-value">{formatPriceDisplay(hotel.price)}</div>
              <div className="price-label">per night</div>
            </div>
          )}
        </div>


        {/* Address and Location */}
        <div className="hotel-address-section">
          <h2 className="section-heading">Location</h2>
          <div className="address-content">
            {hotel.address ? (
              <>
                {hotel.address.lines && hotel.address.lines.length > 0 ? (
                  <div className="address-lines">
                    {hotel.address.lines.map((line, idx) => (
                      <div key={idx} className="address-line">{line}</div>
                    ))}
                  </div>
                ) : null}
                
                <div className="address-city-info">
                  {hotel.address.cityName && <span className="address-item">{hotel.address.cityName}</span>}
                  {hotel.address.stateCode && <span className="address-item">{hotel.address.stateCode}</span>}
                  {hotel.address.postalCode && <span className="address-item">{hotel.address.postalCode}</span>}
                  {hotel.address.countryCode && <span className="address-item">{hotel.address.countryCode}</span>}
                </div>
                
                {(!hotel.address.lines || hotel.address.lines.length === 0) && 
                 !hotel.address.cityName && 
                 !hotel.address.countryCode && (
                  <div className="no-address-message">
                    Address information not available from the hotel.
                  </div>
                )}
              </>
            ) : (
              <div className="no-address-message">
                Location information not available.
              </div>
            )}
            
            {hotel.hotelDistance && (
              <div className="distance-info">
                <span className="distance-value">
                  {hotel.hotelDistance.distance} {hotel.hotelDistance.distanceUnit}
                </span>
                <span className="distance-text">from city center</span>
                {hotel.hotelDistance.direction && (
                  <span className="distance-direction">({hotel.hotelDistance.direction})</span>
                )}
              </div>
            )}
            
            {/* Show search params location if available */}
            {searchParams && searchParams.cityName && (
              <div className="search-location-info">
                <span className="location-label">Search Location:</span>
                <span className="location-value">{searchParams.cityName}</span>
                {searchParams.cityCode && (
                  <span className="location-code">({searchParams.cityCode})</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {hotel.description && (
          <div className="hotel-description-section">
            <h2 className="section-heading">About This Hotel</h2>
            <p className="description-text">{hotel.description}</p>
          </div>
        )}

        {/* Amenities */}
        {hotel.amenities && hotel.amenities.length > 0 && (
          <div className="hotel-amenities-section">
            <h2 className="section-heading">Amenities & Facilities</h2>
            <div className="amenities-grid">
              {hotel.amenities.map((amenity, idx) => (
                <div key={idx} className="amenity-item">
                  {amenity.replace(/_/g, ' ')}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Information */}
        {hotel.contact && (hotel.contact.phone || hotel.contact.email || hotel.contact.fax) && (
          <div className="hotel-contact-section">
            <h2 className="section-heading">Contact Information</h2>
            <div className="contact-info-grid">
              {hotel.contact.phone && (
                <div className="contact-item">
                  <span className="contact-label">Phone:</span>
                  <span className="contact-value">{hotel.contact.phone}</span>
                </div>
              )}
              {hotel.contact.email && (
                <div className="contact-item">
                  <span className="contact-label">Email:</span>
                  <a href={`mailto:${hotel.contact.email}`} className="contact-value-link">
                    {hotel.contact.email}
                  </a>
                </div>
              )}
              {hotel.contact.fax && (
                <div className="contact-item">
                  <span className="contact-label">Fax:</span>
                  <span className="contact-value">{hotel.contact.fax}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Available Rooms & Offers */}
        {hotel.offers && hotel.offers.length > 0 && (
          <div className="hotel-offers-section">
            <h2 className="section-heading">Available Rooms & Offers ({hotel.offers.length})</h2>
            <div className="offers-list-container">
              {hotel.offers.map((offer, idx) => (
                <div key={idx} className="offer-item-card">
                  <div className="offer-header-info">
                    <div className="offer-room-details">
                      <h3 className="offer-room-title">
                        {offer.room?.type || offer.room?.typeEstimated?.category || 'Standard Room'}
                      </h3>
                      {offer.room?.typeEstimated && (
                        <div className="room-specifications">
                          {offer.room.typeEstimated.beds && (
                            <span>{offer.room.typeEstimated.beds} {offer.room.typeEstimated.beds === 1 ? 'Bed' : 'Beds'}</span>
                          )}
                          {offer.room.typeEstimated.bedType && (
                            <span> • {offer.room.typeEstimated.bedType}</span>
                          )}
                        </div>
                      )}
                      {offer.room?.description?.text && (
                        <p className="room-description-text">{offer.room.description.text}</p>
                      )}
                    </div>
                    {offer.price && (
                      <div className="offer-price-info">
                        <div className="offer-price-amount">{formatPriceDisplay(offer.price)}</div>
                        <div className="offer-price-note">Total for stay</div>
                        {offer.price.base && (
                          <div className="offer-price-base">
                            Base: {formatPriceDisplay({...offer.price, total: offer.price.base})}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {offer.policies && (
                    <div className="offer-policies-info">
                      {offer.policies.paymentType && (
                        <div className="policy-info-item">
                          <strong>Payment:</strong> {offer.policies.paymentType}
                        </div>
                      )}
                      {offer.policies.cancellation && (
                        <div className="policy-info-item">
                          <strong>Cancellation:</strong> {
                            offer.policies.cancellation.type === 'FULL_STAY' 
                              ? 'Non-refundable' 
                              : 'Cancellation available'
                          }
                        </div>
                      )}
                      {offer.policies.checkInOut && (
                        <div className="policy-info-item">
                          <strong>Check-in:</strong> {offer.policies.checkInOut.checkIn || 'N/A'} | 
                          <strong> Check-out:</strong> {offer.policies.checkInOut.checkOut || 'N/A'}
                        </div>
                      )}
                    </div>
                  )}

                  {offer.checkInDate && offer.checkOutDate && (
                    <div className="offer-dates-info">
                      <span>Check-in: {formatDate(offer.checkInDate)}</span>
                      <span>Check-out: {formatDate(offer.checkOutDate)}</span>
                    </div>
                  )}

                  {offer.roomQuantity && (
                    <div className="offer-rooms-info">
                      <span>Rooms: {offer.roomQuantity}</span>
                      {offer.guests && (
                        <span> • Guests: {offer.guests.adults || 1} Adult{offer.guests.adults > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  )}

                  <button 
                    className="book-room-button"
                    onClick={() => {
                      // Navigate to booking page with offer details
                      navigate('/hotel-booking', {
                        state: {
                          hotel,
                          offer,
                          searchParams
                        }
                      });
                    }}
                  >
                    Book This Room
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Book Hotel Button */}
        <div className="book-hotel-section">
          <button 
            className="book-hotel-main-button"
            onClick={() => {
              navigate('/hotel-booking', { 
                state: { 
                  hotel,
                  searchParams 
                } 
              });
            }}
          >
            Book Hotel - Starting from {hotel.price ? formatPriceDisplay(hotel.price) : 'N/A'}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HotelDetailsPage;
